import { forwardRef } from 'react';
import type { ReactElement } from 'react';
import Tippy from '@tippyjs/react';
import type { TippyProps } from '@tippyjs/react';
import type { Placement } from 'tippy.js';
import styled from '@emotion/styled';
import * as tokens from '@tokens';
import { ZINDEX } from '@utils/constants';
import { ALLOWED_PLACEMENTS, POPOVER_OFFSET, POPOVER_WIDTH } from './constants';

type AllowedPlacement = (typeof ALLOWED_PLACEMENTS)[number];

export type PopoverPlacement = Extract<Placement, AllowedPlacement>;

export interface BasePopoverContainerProps
  extends Omit<
    TippyProps,
    | 'appendTo'
    | 'arrow'
    | 'children'
    | 'content'
    | 'duration'
    | 'interactive'
    | 'offset'
    | 'placement'
    | 'trigger'
    | 'ref'
  > {
  /**
   * The trigger element.
   */
  children: ReactElement;

  /**
   * Define rule for preferred placement.
   */
  placement?: PopoverPlacement;

  /**
   * z-index of the Tooltip
   */
  zIndex?: number;

  /**
   * The element to append the popover to. Sometimes the popover needs to be
   * appended to a different DOM context due to accessibility, clipping, or
   * z-index issues. It should only be used when needed. By default the popover
   * is appended to the document body.
   */
  appendTo?: HTMLElement | 'parent' | ((ref: Element) => Element);

  /**
   * The content of the popover.
   */
  content: ReactElement;

  /**
   * Styles override.
   */
  className?: string;
}

const StyledTippy = styled(Tippy)`
  max-width: ${POPOVER_WIDTH};
  background-color: ${tokens.color.ui_background};
  color: ${tokens.color.text_default};
  border-radius: ${tokens.radius.m};
  ${tokens.elevation(2)};
  overflow: hidden;

  &[data-animation='fade'][data-state='hidden'] {
    opacity: 0;
  }

  &[data-inertia][data-state='visible'] {
    transition-timing-function: cubic-bezier(0.54, 1.5, 0.38, 1.11);
  }

  .tippy-arrow {
    position: absolute;
    left: 0;
    top: -8px;
    overflow: hidden;
    width: 47px;
    height: 10px;
  }
  .tippy-arrow:before {
    content: '';
    position: absolute;
    border-color: transparent;
    border-style: solid;
    transform: translateX(-50%) translateY(-50%) rotate(45deg);
    background-color: ${tokens.color.ui_background};
    width: 8px;
    height: 8px;
    border-width: unset;
    box-sizing: content-box;
    ${tokens.elevation(2)};
  }

  &[data-placement$='start'] {
    margin-left: -10px;
  }
  &[data-placement$='end'] {
    margin-right: -10px;
  }

  &[data-placement^='top'] > .tippy-arrow {
    top: 100%;
  }
  &[data-placement^='top'] > .tippy-arrow:before {
    transform-origin: center;
    border-width: unset;
    border-top-color: unset;
    top: -3px;
    left: 50%;
  }

  &[data-placement^='bottom'] > .tippy-arrow {
    top: -10px;
  }
  &[data-placement^='bottom'] > .tippy-arrow:before {
    transform-origin: center;
    border-width: unset;
    border-bottom-color: unset;
    top: 13px;
    left: 50%;
  }
`;

/**
 * BasePopoverContainer component
 *
 * **WARNING!** _This component is for internal use in the Design System. If you
 * need to use it for a custom component, please discuss your use case with
 * the Design System squad before proceeding, as they may be able to help
 * you with a better solution._
 *
 * @summary Provides a low-level implementation for the Popover component.
 * @see Popover
 */
export const BasePopoverContainer = forwardRef<
  HTMLElement,
  BasePopoverContainerProps
>(
  (
    {
      children,
      zIndex = ZINDEX.POPOVER,
      appendTo = () => document.body,
      placement = 'top',
      content,
      className,
      ...tippyProps
    }: BasePopoverContainerProps,
    ref,
  ): JSX.Element => {
    return (
      <StyledTippy
        ref={ref}
        // allow to overwrite `trigger` through BasePopover
        trigger="click"
        {...tippyProps}
        interactive
        zIndex={zIndex}
        appendTo={appendTo}
        placement={placement}
        offset={POPOVER_OFFSET}
        content={content}
        className={className}
        popperOptions={{
          modifiers: [
            {
              name: 'flip',
              options: {
                fallbackPlacements: ALLOWED_PLACEMENTS,
                allowedAutoPlacements: ALLOWED_PLACEMENTS,
              },
            },
          ],
        }}
      >
        {children}
      </StyledTippy>
    );
  },
);

BasePopoverContainer.displayName = 'BasePopoverContainer';
